import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { createPrompt } from "../services/PromptsService";
import { usePromptForm } from "../hooks/usePromptForm";

const AddPrompt = ({ open, handleClose, company }) => {
  // Constants
  const TAB_CATEGORIES = [
    { display: "Articles", value: "ARTICLES" },
    { display: "Single Post", value: "SINGLE_POST" },
    { display: "Article Titles", value: "ARTICLE_TITLES" },
    { display: "Article Taglines", value: "ARTICLE_TAGLINES" },
    { display: "Banner Titles", value: "BANNER_TITLES" },
    { display: "News", value: "NEWS" },
    { display: "Weekly News", value: "WEEKLY_NEWS" }
  ];

  const PROMPT_TYPES = [
    { display: "Prompt", value: "PROMPT" },
    { display: "Format", value: "FORMAT" },
    { display: "Sub Format", value: "SUB_FORMAT" }
  ];

  const CREATE_OPTIONS = [
    { display: "Create New", value: "CREATE_NEW" },
    { display: "Override Existing", value: "OVERRIDE" }
  ];

  // Use custom hook for form logic
  const {
    formData,
    formatData,
    systemPrompts,
    isLoading,
    handleChange,
    isFormValid,
    getSubmitData,
    resetForm
  } = usePromptForm(company, open);

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await createPrompt(getSubmitData());
      handleBack();
    } catch (error) {
      console.error("Error creating prompt:", error);
    }
  };

  // Handle dialog close
  const handleBack = () => {
    resetForm();
    handleClose();
  };

  return (
      <Dialog
          fullScreen
          open={open}
          onClose={handleClose}
          PaperProps={{
            component: 'form',
            onSubmit: handleSubmit,
          }}
      >
        <DialogTitle>Create Prompt</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
            {/* Name Field */}
            <TextField
                fullWidth
                margin="dense"
                id="name"
                disabled={
                    (company && formData.type === "PROMPT") ||
                    (company && formData.createOption === "OVERRIDE" &&
                        (formData.type === "FORMAT" || formData.type === "SUB_FORMAT"))
                }
                name="name"
                label="Name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
            />

            {/* Category Selection */}
            <FormControl fullWidth margin="dense">
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                  labelId="category-label"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category"
              >
                {TAB_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.display}
                    </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Prompt Type Selection */}
            <FormControl fullWidth margin="dense">
              <InputLabel id="type-label">Prompt Type</InputLabel>
              <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  label="Prompt Type"
              >
                {PROMPT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.display}
                    </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Create/Override Option - Only show when relevant */}
            {company && (formData.type === "FORMAT" || formData.type === "SUB_FORMAT") && (
                <FormControl fullWidth margin="dense">
                  <InputLabel id="create-option-label">Creation Option</InputLabel>
                  <Select
                      labelId="create-option-label"
                      id="createOption"
                      name="createOption"
                      value={formData.createOption}
                      onChange={handleChange}
                      label="Creation Option"
                  >
                    {CREATE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.display}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
            )}

            {/* Format Selection - For FORMAT override */}
            {company && formData.type === "FORMAT" && formData.createOption === "OVERRIDE" && (
                <FormControl fullWidth margin="dense">
                  <InputLabel id="existing-format-label">Existing Format</InputLabel>
                  <Select
                      labelId="existing-format-label"
                      id="existingFormat"
                      name="existingFormat"
                      value={formData.existingFormat}
                      onChange={handleChange}
                      label="Existing Format"
                      disabled={isLoading || formatData.existingFormats.length === 0}
                  >
                    {formatData.existingFormats.map((format) => (
                        <MenuItem key={format.id} value={format.id}>
                          {format.name}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
            )}

            {/* Format & Subformat Selections - For SUB_FORMAT */}
            {formData.type === "SUB_FORMAT" && (
                <>
                  {/* Only show format selection when creating new */}
                  {formData.createOption === "CREATE_NEW" && (
                      <FormControl fullWidth margin="dense">
                        <InputLabel id="format-label">Format</InputLabel>
                        <Select
                            labelId="format-label"
                            id="format"
                            name="format"
                            value={formData.format}
                            onChange={handleChange}
                            label="Format"
                            disabled={isLoading || formatData.formats.length === 0}
                        >
                          {formatData.formats.map((format) => (
                              <MenuItem key={format.id} value={format.id}>
                                {format.name}
                              </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                  )}

                  {/* For overriding existing subformats */}
                  {company && formData.createOption === "OVERRIDE" && (
                      <FormControl fullWidth margin="dense">
                        <InputLabel id="existing-subformat-label">Existing Sub Format</InputLabel>
                        <Select
                            labelId="existing-subformat-label"
                            id="existingFormat"
                            name="existingFormat"
                            value={formData.existingFormat}
                            onChange={handleChange}
                            label="Existing Sub Format"
                            disabled={isLoading || formatData.subFormats.length === 0}
                        >
                          {formatData.subFormats.map((subFormat) => (
                              <MenuItem key={subFormat.id} value={subFormat.id}>
                                {subFormat.name}
                              </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                  )}
                </>
            )}

            {/* System Prompt Selection - For PROMPT */}
            {formData.type === "PROMPT" && company && (
                <FormControl fullWidth margin="dense">
                  <InputLabel id="system-prompt-label">Available Prompt</InputLabel>
                  <Select
                      labelId="system-prompt-label"
                      id="systemPrompt"
                      name="systemPrompt"
                      value={formData.systemPrompt}
                      onChange={handleChange}
                      label="Available Prompt"
                      disabled={isLoading || systemPrompts.length === 0}
                  >
                    {systemPrompts.map((prompt) => (
                        <MenuItem key={prompt.id} value={prompt.id}>
                          {prompt.name}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={handleBack}>
            Cancel
          </Button>
          <Button
              variant="contained"
              type="submit"
              disabled={!isFormValid()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default AddPrompt;
